import { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import useKey from '@rooks/use-key';
import { Eye, EyeOff, Lock, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../sporttia/all';
import { useQuery } from '../../lib/hooks';
import useUsersService from '../../services/UsersService';
import Input2FA from '../../components/Input2FA';
import AuthScreen, {
    AuthBrandHeader,
    authButtonSx,
    authFieldSx,
} from './AuthScreen';
import LoginAside from './LoginAside';

interface LoginResponse {
    user?: {
        id: number;
        role: string;
        status: string;
        signature?: string;
    };
    twoFactorCodeSent?: boolean;
    text2FA?: string;
}

interface ErrorResponse {
    error: {
        msg: string;
    };
}

export default function Login() {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { redirect } = useQuery();
    const [loginLoading, setLoginLoading] = useState(false);
    const [inProgress2FA, setInProgress2FA] = useState(false);
    const [text2FA, setText2FA] = useState<string | null>(null);
    const [isVerifying2FA, setIsVerifying2FA] = useState(false);
    const [isResending2FA, setIsResending2FA] = useState(false);

    const twoFactorAuthMutation = useUsersService().usePost2FACode();

    useKey(['Enter'], () => {
        doLogin();
    });

    const processLoginSuccess = (response: LoginResponse) => {
        setLoginLoading(false);
        if (response.user) {
            if (response.user.role !== 'SPORTCENTER') {
                cxt.showMessage('E', t('login.roleNotAllowed'));
                return;
            }

            switch (response.user.status) {
                case 'WAIT_EMAIL':
                    navigate('/email/activation', {
                        state: { user: response.user },
                    });
                    break;
                case 'NOT_CONFIRM':
                    navigate(
                        `/privacy/confirmation/${response.user.id}/${response.user.signature}`,
                    );
                    break;
                case 'ACTIVE':
                    cxt.setLoggedUser(response.user);
                    break;
                default:
                    break;
            }
        } else if (response.twoFactorCodeSent) {
            setInProgress2FA(true);
            setText2FA(response.text2FA || null);
        }
    };

    const doLogin = () => {
        setLoginLoading(true);
        cxt.api('POST', '/login', {
            params: {
                login,
                password,
            },
            error: (r: unknown) => {
                const errorResponse = r as ErrorResponse;
                cxt.showMessage('E', errorResponse.error.msg);
                setLoginLoading(false);
            },
            success: (r: LoginResponse) => {
                processLoginSuccess(r);
            },
        });
    };

    useEffect(() => {
        if (cxt.user && cxt.user?.id) {
            if (redirect) {
                const redirectUrl = Array.isArray(redirect)
                    ? redirect[0]
                    : redirect;
                navigate(decodeURIComponent(redirectUrl as string));
            } else if (location.state?.from?.pathname) {
                navigate(location.state.from.pathname);
            } else {
                navigate('/');
            }
        }
    }, [cxt.user, navigate, redirect, location.state?.from?.pathname]);

    return (
        <AuthScreen width="5xl">
            {/*
                A partir de `lg` la pantalla se parte en dos: presentación a la
                izquierda (encabezado arriba, ventajas debajo) y formulario a la
                derecha, centrado entre las dos filas. En móvil manda el orden
                del DOM —encabezado, formulario, ventajas— porque quien llega
                aquí viene a entrar y las ventajas no pueden empujar los campos
                por debajo del pliegue.
            */}
            <Box className="grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
                <Box
                    component="header"
                    className="text-center lg:col-start-1 lg:row-start-1 lg:text-left"
                >
                    <h1 className="text-3xl font-semibold sm:text-4xl lg:text-5xl">
                        {t('login.managerTitle')}
                    </h1>
                    <p className="text-auth-muted mt-2 text-base">
                        {t('login.managerSubtitle')}
                    </p>
                </Box>

                <Box className="mx-auto w-full max-w-md lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-center">
                    <AuthBrandHeader />

                    <Box className="flex flex-col gap-4">
                        <TextField
                            autoFocus
                            fullWidth
                            hiddenLabel
                            name="login"
                            autoComplete="username"
                            placeholder={t('login.loginOrEmail')}
                            value={login}
                            onChange={(e) => setLogin(e.target.value)}
                            sx={authFieldSx}
                            slotProps={{
                                htmlInput: {
                                    'aria-label': t('login.loginOrEmail'),
                                },
                                input: {
                                    startAdornment: (
                                        <User
                                            size={20}
                                            className="text-auth-field-muted mr-3 shrink-0"
                                        />
                                    ),
                                },
                            }}
                        />

                        <TextField
                            fullWidth
                            hiddenLabel
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            placeholder={t('user.password')}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            sx={authFieldSx}
                            slotProps={{
                                htmlInput: {
                                    'aria-label': t('user.password'),
                                },
                                input: {
                                    startAdornment: (
                                        <Lock
                                            size={20}
                                            className="text-auth-field-muted mr-3 shrink-0"
                                        />
                                    ),
                                    endAdornment: (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowPassword(
                                                    (current) => !current,
                                                )
                                            }
                                            aria-label={t(
                                                showPassword
                                                    ? 'login.hidePassword'
                                                    : 'login.showPassword',
                                            )}
                                            className="text-auth-field-muted hover:text-auth-field-foreground shrink-0 cursor-pointer bg-transparent transition-colors"
                                        >
                                            {showPassword ? (
                                                <EyeOff size={20} />
                                            ) : (
                                                <Eye size={20} />
                                            )}
                                        </button>
                                    ),
                                },
                            }}
                        />

                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={loginLoading}
                            onClick={() => doLogin()}
                            sx={authButtonSx}
                            startIcon={
                                loginLoading ? (
                                    <CircularProgress
                                        size={20}
                                        color="inherit"
                                    />
                                ) : null
                            }
                            className="h-14 rounded-xl text-base font-semibold normal-case"
                        >
                            {loginLoading
                                ? t('login.entering')
                                : t('login.enter')}
                        </Button>

                        <p className="pt-2 text-center">
                            <a
                                href="/recover"
                                className="text-auth-foreground text-sm no-underline hover:underline"
                            >
                                {t('login.recoverPassword')}
                            </a>
                        </p>
                    </Box>
                </Box>

                <LoginAside className="mx-auto w-full max-w-md lg:col-start-1 lg:row-start-2 lg:mx-0 lg:max-w-none" />
            </Box>

            {inProgress2FA && (
                <Input2FA
                    text2FA={text2FA || ''}
                    loading={isVerifying2FA}
                    resending={isResending2FA}
                    onSave={(code: string) => {
                        setIsVerifying2FA(true);
                        twoFactorAuthMutation.mutate(
                            {
                                params: {
                                    twoFactorCode: code,
                                    login,
                                },
                            },
                            {
                                onError: (error: unknown) => {
                                    const err = error as { msg: string };
                                    cxt.showMessage('E', err.msg);
                                    setIsVerifying2FA(false);
                                },
                                onSuccess: (response: unknown) => {
                                    setIsVerifying2FA(false);
                                    processLoginSuccess(
                                        response as LoginResponse,
                                    );
                                },
                            },
                        );
                    }}
                    onResentCode={() => {
                        setIsResending2FA(true);
                        twoFactorAuthMutation.mutate(
                            {
                                params: {
                                    resent2FACode: true,
                                    login,
                                },
                            },
                            {
                                onError: (error: unknown) => {
                                    const err = error as { msg: string };
                                    cxt.showMessage('E', err.msg);
                                    setIsResending2FA(false);
                                },
                                onSuccess: () => {
                                    cxt.showMessage(
                                        'S',
                                        t('user.resent2FASuccess'),
                                    );
                                    setIsResending2FA(false);
                                },
                            },
                        );
                    }}
                    onClose={() => setInProgress2FA(false)}
                />
            )}
        </AuthScreen>
    );
}
